import Dropdown from "@/Components/ui/Dropdown";
import Icon from "@/Components/ui/Icon";
import { Link, usePage, router } from "@inertiajs/react";
import { Menu } from "@headlessui/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import type { PageProps } from "@/types";

dayjs.extend(relativeTime);

interface NotificationData {
    title: string;
    description: string;
}

interface NotificationItem {
    id: string;
    data: NotificationData;
    created_at: string;
    read_at: string | null;
}

interface NotificationPageProps extends PageProps {
    notifications: NotificationItem[];
}

interface NotifyLabelProps {
    count: number;
}

const notifyLabel = ({ count }: NotifyLabelProps) => {
    return (
        <span className="relative lg:h-[32px] lg:w-[32px] lg:bg-slate-100 text-slate-900 lg:dark:bg-slate-900 dark:text-white cursor-pointer rounded-full text-[20px] flex flex-col items-center justify-center">
            <Icon icon="heroicons-outline:bell" className="animate-tada" />
            {count > 0 && (
                <span className="absolute lg:right-0 lg:top-0 -top-2 -right-2 h-4 w-4 bg-red-500 text-[8px] font-semibold flex flex-col items-center justify-center rounded-full text-white z-[99]">
                    {count}
                </span>
            )}
        </span>
    );
};

const Notification = () => {
    const { notifications, auth } = usePage<NotificationPageProps>().props;
    const user = auth?.user;

    const readNotification = (item: NotificationItem) => {
        if (!user) return;
        router.put(
            route("users.notifications.update", {
                user: user.id,
                notification: item.id,
            }),
        );
    };

    if (!user) {
        return null;
    }

    return (
        <Dropdown
            classMenuItems="md:w-[300px] top-[58px]"
            label={notifyLabel({ count: notifications?.length ?? 0 })}
        >
            <div className="flex justify-between px-4 py-4 border-b border-slate-100 dark:border-slate-600">
                <div className="text-sm text-slate-800 dark:text-slate-200 font-medium leading-6">
                    Notifications
                </div>
                <div className="text-slate-800 dark:text-slate-200 text-xs md:text-right">
                    <Link
                        href={route("users.notifications.index", {
                            user: user.id,
                        })}
                        className="underline"
                    >
                        View all
                    </Link>
                </div>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {!notifications?.length && (
                    <Menu.Item>
                        {({ active }) => (
                            <div
                                className={`${
                                    active
                                        ? "bg-slate-100 dark:bg-slate-700 dark:bg-opacity-70 text-slate-800"
                                        : "text-slate-600 dark:text-slate-300"
                                } block w-full px-4 py-2 text-sm  cursor-pointer`}
                            >
                                <div className="flex ltr:text-left rtl:text-right">
                                    <div className="flex-1">
                                        <div
                                            className={`${
                                                active
                                                    ? "text-slate-500 dark:text-slate-200"
                                                    : " text-slate-600 dark:text-slate-300"
                                            } text-sm text-center leading-4 py-2`}
                                        >
                                            You have no notifications
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Menu.Item>
                )}
                {notifications?.map((item, i) => (
                    <Menu.Item key={i} as="div">
                        {({ active }) => (
                            <button
                                type="button"
                                onClick={() => readNotification(item)}
                                className={`${
                                    active
                                        ? "bg-slate-100 dark:bg-slate-700 dark:bg-opacity-70 text-slate-800"
                                        : "text-slate-600 dark:text-slate-300"
                                } block w-full px-4 py-2 text-sm cursor-pointer text-left`}
                            >
                                <div className="flex ltr:text-left rtl:text-right">
                                    <div className="flex-1">
                                        <div
                                            className={`${
                                                active
                                                    ? "text-slate-600 dark:text-slate-300"
                                                    : " text-slate-600 dark:text-slate-300"
                                            } text-sm`}
                                        >
                                            {item.data.title}
                                        </div>
                                        <div
                                            className={`${
                                                active
                                                    ? "text-slate-500 dark:text-slate-200"
                                                    : " text-slate-600 dark:text-slate-300"
                                            } text-xs leading-4`}
                                        >
                                            {item.data.description}
                                        </div>
                                        <div className="text-slate-400 dark:text-slate-400 text-xs mt-1">
                                            {dayjs(item.created_at).fromNow()}
                                        </div>
                                    </div>
                                    {!item.read_at && (
                                        <div className="flex-0">
                                            <span className="h-[10px] w-[10px] bg-danger-500 border border-white dark:border-slate-400 rounded-full inline-block"></span>
                                        </div>
                                    )}
                                </div>
                            </button>
                        )}
                    </Menu.Item>
                ))}
            </div>
        </Dropdown>
    );
};

export default Notification;
